import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Chip,
} from '@mui/material';
import { Fade } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import Info from './Info';
import { useAdminLanguage } from './AdminI18n';

const LicenseAdmin = () => {
  const { t } = useAdminLanguage();
  const [licenseKey, setLicenseKey] = useState('');
  const [message, setMessage] = useState('');
  const [expirationDate, setExpirationDate] = useState(null);
  const [daysRemaining, setDaysRemaining] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [isLicenseValid, setIsLicenseValid] = useState(null);
  const [licenseReason, setLicenseReason] = useState('');
  const [guardiaAccess, setGuardiaAccess] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const fetchLicenseData = async () => {
      try {
        const response = await fetch('/api/license', { headers: getAuthHeaders() });
        if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);
        const data = await response.json();
        setLicenseKey(data.licenseKey || '');
        setIsLicenseValid(data.configured ? data.valid === true : null);
        setLicenseReason(data.reason || '');

        if (data.license) {
          const licenseInfo = data.license;
          const expDate = new Date(licenseInfo.expirationDate || licenseInfo.expiration);
          setExpirationDate(expDate);
          const today = new Date();
          const timeDiff = expDate - today;
          const days = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
          setDaysRemaining(days);
          setIsExpired(days < 0);
          setGuardiaAccess(licenseInfo.guardiaAccess);
        }
      } catch (err) {
        console.error('Erreur lors de la récupération/verification :', err);
        setError('Une erreur est survenue lors du chargement des données.');
      } finally {
        setLoading(false);
      }
    };

    fetchLicenseData();
  }, []);

  const handleChange = (e) => setLicenseKey(e.target.value);

  const handleClearLicenseKey = () => {
    setLicenseKey('');
    setMessage('');
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage('');
    setError(null);

    try {
      const response = await fetch('/api/license', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ licenseKey }),
      });
      const data = await response.json();

      if (response.ok) {
        setMessage('Clef produit enregistrée avec succès.');
        setTimeout(() => window.location.reload(), 1000);
      } else {
        setError(data.error || 'Erreur lors de l’enregistrement de la clef.');
      }
    } catch (err) {
      console.error('Erreur lors de l’enregistrement :', err);
      setError('Erreur lors de la communication avec le serveur.');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusChip = () => {
    if (isLicenseValid === false) {
      return <Chip icon={<ErrorIcon />} label={licenseReason || 'Invalide'} color="error" />;
    } else if (isExpired) {
      return <Chip icon={<ErrorIcon />} label="Expirée" color="error" />;
    } else if (daysRemaining !== null && daysRemaining <= 7) {
      return <Chip icon={<WarningIcon />} label="Expire bientôt" color="warning" />;
    } else if (daysRemaining !== null) {
      return <Chip icon={<CheckCircleIcon />} label="Active" color="success" />;
    }
    return null;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in={!loading} timeout={500}>
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            align="center"
            color="primary"
            sx={{ fontWeight: 'bold', mb: 4 }}
          >
            {t('Gestion des licences')}
          </Typography>

          <Info type="license" />

          {loading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <Grid container spacing={3}>
              {/* Section Formulaire */}
              <Grid item xs={12} md={6}>
                <Box component="form" onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    label={t('Clef produit')}
                    value={licenseKey}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    margin="normal"
                    disabled={submitting}
                    helperText={t('Entrez une clef valide pour activer votre licence.')}
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Button
                      type="button"
                      variant="outlined"
                      color="inherit"
                      fullWidth
                      disabled={submitting || !licenseKey}
                      onClick={handleClearLicenseKey}
                      sx={{
                        py: 1.5,
                        fontSize: '1rem',
                        textTransform: 'none',
                      }}
                    >
                      {t('Effacer')}
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth
                      disabled={submitting}
                      startIcon={submitting && <CircularProgress size={20} />}
                      sx={{
                        py: 1.5,
                        fontSize: '1rem',
                        textTransform: 'none',
                        transition: 'all 0.3s',
                        '&:hover': { boxShadow: 6 },
                      }}
                    >
                      {submitting ? t('Enregistrement...') : t('Enregistrer la clef')}
                    </Button>
                  </Box>
                </Box>

                {message && (
                  <Alert severity="success" sx={{ mt: 2 }} icon={<CheckCircleIcon />}>
                    {t(message)}
                  </Alert>
                )}
                {error && (
                  <Alert severity="error" sx={{ mt: 2 }} icon={<ErrorIcon />}>
                    {t(error)}
                  </Alert>
                )}
              </Grid>

              {/* Section Informations Licence */}
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {t('État de la licence actuelle')}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {t("Date d'expiration")} :{' '}
                    <strong>
                      {expirationDate ? expirationDate.toLocaleDateString() : t('Non défini')}
                    </strong>
                  </Typography>
                  {daysRemaining !== null && (
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {t('Jours restants')} : <strong>{daysRemaining}</strong>
                    </Typography>
                  )}
                  {guardiaAccess !== null && (
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {t('Accès GuardIA')} : <strong>{guardiaAccess ? t('Activé') : t('Désactivé')}</strong>
                    </Typography>
                  )}
                  {isLicenseValid === false && licenseReason && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {t(licenseReason)}
                    </Alert>
                  )}
                  <Box sx={{ mt: 2 }}>{getStatusChip()}</Box>
                </Box>
              </Grid>
            </Grid>
          )}
        </Box>
      </Fade>
    </Container>
  );
};

export default LicenseAdmin;